import { Flex, Input, SimpleGrid, Switch } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useCustomers } from "../../api/hooks/customer/useCustomers.ts";
import { useImportCustomer } from "../../api/hooks/import/useImportCustomer.ts";
import { useLimits } from "../../api/hooks/limit/useLimits.ts";
import { ImportModal } from "../../components/ImportModal/ImportModal.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import { MultiSelect } from "../../components/MultiSelect.tsx";
import Table from "../../components/Table/Table.tsx";
import { SortDirection } from "../../components/Table/types.ts";
import Title from "../../components/Title/Title.tsx";
import { AppRoute } from "../../constants.ts";
import {
  CustomerFreezeStatus,
  CustomerOrderBy,
  ImportResult,
  ImportType,
  OrderBy,
} from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import { activeLimitStatusOptions } from "../../types/limit.ts";
import { getApiOrderBy } from "../../utils/api.ts";
import {
  getCompanyOptions,
  getSalesNameOptions,
  getSalesTeamOptions,
} from "../../utils/company.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import { isBababosTenant } from "../../utils/user.ts";
import { useCustomersTableData } from "./hooks/useCustomersTableData.tsx";
import { CustomerFilterValue } from "./types.ts";
import { getCustomerApiFilter, getLimitApiFilter } from "./utils.ts";

const CustomerDatabasePage = () => {
  const navigate = useNavigate();

  const { user } = useCurrentUserContext();

  const [filter, setFilter] = useState<CustomerFilterValue>({});
  const apiFilter = useMemo(() => {
    return getCustomerApiFilter(filter);
  }, [filter]);
  const apiLimitFilter = useMemo(() => {
    return getLimitApiFilter(filter);
  }, [filter]);

  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<CustomerOrderBy>({
    name: OrderBy.Asc,
  });

  const [
    {
      data: customersResponse,
      fetching: customersFetching,
      error: customersError,
    },
    refresh,
  ] = useCustomers({
    filter: { ...apiFilter, parentId: null },
    orderBy,
    page,
  });
  const customers = customersResponse?.customers.data;
  const pageCount = getPagesCount(customersResponse?.customers?.total);

  const [
    {
      data: customerOptionsResponse,
      fetching: customerOptionsFetching,
      error: customerOptionsError,
    },
  ] = useCompanyOptions();
  const companyOptions = customerOptionsResponse?.customers.data;

  const [
    { data: limitsResponse, fetching: limitsFetching, error: limitsError },
  ] = useLimits({ filter: apiLimitFilter });
  const limits = limitsResponse?.limits.data;

  const error = customersError || limitsError || customerOptionsError;

  const [{ fetching: loadingImport }, importCustomers] =
    useMutationNotificationWrapper(useImportCustomer(), {
      success: { show: false },
    });

  const [columns, rows] = useCustomersTableData({
    customers,
    limits,
  });

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleFilterChange = useCallback(
    (key: keyof CustomerFilterValue, value: unknown) => {
      setFilter((prevFilter) => {
        return { ...prevFilter, [key]: value };
      });
      setPage(1);
    },
    []
  );

  const handleOnSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getApiOrderBy(sort));
    },
    [setOrderBy]
  );

  const handleImportCustomers = useCallback(
    async ({ file }: { file: string }) => {
      return new Promise<ImportResult | undefined>((resolve) =>
        importCustomers({ input: { file } }).then((data) => {
          if (data.data?.importCustomer.status === "success") {
            notifications.show({
              message: "Customers have been imported successfully.",
              variant: "success",
            });
          } else {
            notifications.show({
              message: "Customers Import failed.",
              variant: "error",
            });
          }
          resolve(data.data?.importCustomer);
        })
      );
    },
    [importCustomers]
  );

  if (error) {
    navigate(AppRoute.Home);
    notifications.show({
      message: "Something went wrong while trying to fetch data.",
      variant: "error",
    });
  }

  return (
    <Layout>
      <Flex direction="column" gap={{ base: 24, sm: 20 }}>
        <Flex gap={20} justify="space-between">
          <Flex gap={20}>
            <Title size="h1">Customer Database</Title>
          </Flex>
        </Flex>
        {!isBababosTenant(user) && (
          <ImportModal
            options={{
              title: "Upload Customer Data",
              type: ImportType.Customer,
              csvTemplateFilename: "upload_customers_template.csv",
              csvTemplateHeaders: [
                { key: "name", label: "Name" },
                { key: "partnerCode", label: "Partner Code" },
                { key: "website", label: "Website" },
                { key: "address", label: "Address" },
                { key: "salesTeamName", label: "Sales Team" },
                { key: "salesName", label: "Sales Name" },
                { key: "picName", label: "PIC Name" },
                { key: "picRole", label: "PIC Role" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "mobile", label: "Mobile" },
              ],
            }}
            loading={loadingImport}
            onImport={handleImportCustomers}
            onRefresh={refresh}
          />
        )}
        <Flex direction="column" gap={16}>
          <SimpleGrid
            w="100%"
            cols={isBababosTenant(user) ? 3 : 4}
            breakpoints={[
              { maxWidth: "sm", cols: 2 },
              { maxWidth: "lg", cols: 3 },
            ]}
            spacing={20}
            verticalSpacing={12}
          >
            <MultiSelect
              size="m"
              clearable
              searchable
              label="Customer Name"
              placeholder="Select Customer Name"
              loading={customerOptionsFetching}
              data={getCompanyOptions(companyOptions)}
              onChange={(value) =>
                handleFilterChange(
                  "id",
                  value.length ? value.map(Number) : null
                )
              }
            />
            <MultiSelect
              size="m"
              clearable
              label="Customer Status"
              placeholder="Select Customer Status"
              data={[
                { value: CustomerFreezeStatus.Inactive, label: "Active" },
                { value: CustomerFreezeStatus.Active, label: "Freeze" },
              ]}
              onChange={(value) => handleFilterChange("freezeStatus", value)}
            />
            {isBababosTenant(user) && (
              <MultiSelect
                size="m"
                clearable
                label="Limit Status"
                placeholder="Select Limit Status"
                data={activeLimitStatusOptions}
                onChange={(value) => handleFilterChange("limitStatuses", value)}
              />
            )}
            <MultiSelect
              size="m"
              clearable
              searchable
              label="Sales Name"
              placeholder="Select Sales Name"
              data={getSalesNameOptions(companyOptions)}
              onChange={(value) => handleFilterChange("salesName", value)}
            />
            <MultiSelect
              size="m"
              clearable
              searchable
              label="Sales Team"
              placeholder="Select Sales Team"
              data={getSalesTeamOptions(companyOptions)}
              onChange={(value) => handleFilterChange("salesTeam", value)}
            />
            {isBababosTenant(user) && (
              <Input.Wrapper label="Show only Customer with Limit">
                <Switch
                  variant="yellow"
                  checked={filter.hasLimits}
                  size="lg"
                  onClick={() =>
                    handleFilterChange("hasLimits", !filter.hasLimits)
                  }
                />
              </Input.Wrapper>
            )}
          </SimpleGrid>
          <Table
            withColumnBorders
            columns={columns}
            rows={rows}
            verticalAlignment="top"
            defaultSort={{
              columnAccessor: "id",
              direction: SortDirection.desc,
            }}
            loading={customersFetching || limitsFetching}
            pagination={{
              pageCount: pageCount,
              page: page,
              onPageChange: handlePageChange,
            }}
            pinLastColumn
            onSortChange={handleOnSortChange}
          />
        </Flex>
      </Flex>
    </Layout>
  );
};

export default CustomerDatabasePage;
